import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faBuilding,
  faUserTie,
  faEnvelope,
  faPhone,
  faLocationDot,
  faMapMarkedAlt,
  faClipboardList,
  faWarehouse,
} from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';
import { USER_ROLES } from '../../utils/constants';
import PortalAccessCard from './PortalAccessCard';

/**
 * InfoBlock
 *
 * Small reusable piece that displays an icon, a label and a value. Used by
 * `CustomerDetails` to present individual fields in a consistent visual block.
 * Props:
 * - icon: FontAwesome icon object
 * - label: short label text shown above the value
 * - value: field value (falls back to em dash when falsy)
 */
const InfoBlock = ({ icon, label, value }) => {
  const { t } = useTranslation();
  const emDash = t('common.symbols.emDash');
  return (
    <div
      className="flex items-start gap-3 rounded-xl border px-4 py-4"
      style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
    >
      <div
        className="flex h-10 w-10 items-center justify-center rounded-lg"
        style={{
          backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.ACTIVE, 0.15),
          color: UI_COLORS.NAVIGATION.ACTIVE,
        }}
      >
        <FontAwesomeIcon icon={icon} />
      </div>
      <div className="flex-1">
        <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
          {label}
        </p>
        <p className="mt-1 text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
          {value || emDash}
        </p>
      </div>
    </div>
  );
};

/**
 * formatDateTime(value, emDash)
 *
 * Safely formats a date/time value for display. Returns the provided em-dash
 * when the value is falsy. If `new Date(value)` fails, the original value is
 * returned as a last resort.
 */
const formatDateTime = (value, emDash) => {
  if (!value) return emDash;
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return value;
  }
};

/**
 * CustomerDetails
 *
 * Modal that shows detailed read-only information about a customer.
 * Props:
 * - isOpen: whether the modal is visible
 * - onClose: callback when the close button is clicked
 * - customer: object containing customer fields (companyName, email, etc.)
 * - loading: when true, shows a loading skeleton instead of data
 */
const CustomerDetails = ({ isOpen, onClose, customer, loading = false, onAddWarehouse, userRole }) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  const strings = t('customers.details', { returnObjects: true });
  const emDash = t('common.symbols.emDash');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
    >
      <div className="w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div
          className="flex items-center justify-between border-b px-6 py-5"
          style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
        >
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
              {strings.header.sectionTitle}
            </p>
            <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {customer?.companyName || strings.header.fallbackTitle}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 transition-all duration-200 hover:scale-105"
            style={{
              color: UI_COLORS.TEXT.MUTED,
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1),
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="max-h-[75vh] overflow-y-auto px-6 py-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div
                className="h-12 w-12 animate-spin rounded-full border-4 border-b-transparent"
                style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.ACTIVE, 0.4) }}
              />
              <p className="mt-4 text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                {strings.loading}
              </p>
            </div>
          ) : !customer ? (
            <div className="flex flex-col items-center justify-center gap-4 rounded-2xl border px-6 py-16 text-center" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
              <FontAwesomeIcon icon={faBuilding} size="2x" style={{ color: UI_COLORS.TEXT.MUTED }} />
              <h3 className="text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {strings.empty.title}
              </h3>
              <p className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                {strings.empty.message}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <InfoBlock icon={faBuilding} label={strings.fields.companyName} value={customer.companyName} />
                <InfoBlock icon={faUserTie} label={strings.fields.ownerName} value={customer.ownerName} />
                <InfoBlock icon={faClipboardList} label={strings.fields.customerType} value={customer.customerType} />
                <InfoBlock icon={faEnvelope} label={strings.fields.email} value={customer.email} />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <InfoBlock icon={faPhone} label={strings.fields.phoneNumber1} value={customer.phoneNumber1} />
                <InfoBlock icon={faPhone} label={strings.fields.phoneNumber2} value={customer.phoneNumber2} />
                <InfoBlock icon={faClipboardList} label={strings.fields.gstNumber} value={customer.gstNumber} />
                <InfoBlock icon={faClipboardList} label={strings.fields.tallyName} value={customer.tallyName} />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <InfoBlock icon={faLocationDot} label={strings.fields.address1} value={customer.address1} />
                <InfoBlock icon={faLocationDot} label={strings.fields.address2} value={customer.address2} />
                <InfoBlock icon={faMapMarkedAlt} label={strings.fields.city} value={customer.city} />
                <InfoBlock icon={faMapMarkedAlt} label={strings.fields.state} value={customer.state} />
                <InfoBlock icon={faMapMarkedAlt} label={strings.fields.country} value={customer.country} />
                <InfoBlock icon={faMapMarkedAlt} label={strings.fields.zipCode} value={customer.zipCode} />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <InfoBlock icon={faClipboardList} label={strings.fields.deliveryLocation} value={customer.deliveryLocation} />
                <InfoBlock icon={faClipboardList} label={strings.fields.creditPeriod} value={customer.creditPeriod ?? emDash} />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <InfoBlock icon={faClipboardList} label={strings.fields.status} value={customer.isActive ? t('customers.statuses.active') : t('customers.statuses.inactive')} />
                <InfoBlock icon={faClipboardList} label={strings.fields.createdAt} value={formatDateTime(customer.createdAt, emDash)} />
                <InfoBlock icon={faClipboardList} label={strings.fields.updatedAt} value={formatDateTime(customer.updatedAt, emDash)} />
              </div>

              {userRole === USER_ROLES.SUPER_ADMIN && customer.id && (
                <PortalAccessCard
                  customerId={customer.id}
                  defaultEmail={customer.email}
                  defaultMobile={customer.phoneNumber1}
                />
              )}
            </div>
          )}
        </div>

        <div
          className="flex items-center justify-between border-t px-6 py-4"
          style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
        >
          {onAddWarehouse && customer ? (
            <button
              type="button"
              onClick={() => onAddWarehouse(customer.id)}
              className="inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold transition-all duration-200 hover:scale-105"
              style={{
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.background, 0.1),
                color: UI_COLORS.BUTTONS.PRIMARY.background,
                border: `1px solid ${colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.background, 0.3)}`,
              }}
            >
              <FontAwesomeIcon icon={faWarehouse} />
              Add Warehouse
            </button>
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl px-5 py-3 font-semibold transition-all duration-200 hover:scale-105"
            style={{
              backgroundColor: 'transparent',
              color: UI_COLORS.TEXT.MUTED,
              border: `1px solid ${UI_COLORS.BORDERS.MEDIUM}`,
            }}
          >
            {strings.buttons.close}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetails;
